import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { from, Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { NavController } from '@ionic/angular';
import { SessionQuery } from '@state/session/session.query';
import { SessionService } from '@state/session/session.service';
import { SchedulerSessionService } from '@core/services/scheduler-session/scheduler-session.service';
import { AES_URL_STORAGE_KEY, SESSION_STORAGE_KEY } from '@core/constants';
import { Preferences } from '@capacitor/preferences';

/**
 * Protege la ruta /scheduler/:hubspotClientId.
 *
 * Flujo:
 * 1. Si el agente ya tiene sesión activa → permite el paso.
 * 2. Si tiene credenciales en Preferences → las restaura y permite el paso.
 * 3. Si no tiene sesión → guarda el hubspotClientId en SchedulerSessionService
 *    y redirige al login para que, al volver, el Container lo recupere.
 */
@Injectable({ providedIn: 'root' })
export class SchedulerAuthGuard {

    constructor(
        private navController: NavController,
        private sessionQuery: SessionQuery,
        private sessionService: SessionService,
        private schedulerSessionService: SchedulerSessionService
    ) {}

    canActivate(
        next: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<boolean | UrlTree> | boolean {

        // Guardar el ID del cliente antes de cualquier redirección
        const hubspotClientId = next.paramMap.get('hubspotClientId');
        if (hubspotClientId) {
            this.schedulerSessionService.saveClientId(hubspotClientId);
        }

        // Si ya hay sesión en memoria, pasa directamente
        if (this.sessionQuery.logged) {
            return true;
        }

        // Intento de restaurar sesión desde Preferences (Capacitor)
        return from(this.getStoredCredentials()).pipe(
            switchMap(([aesUrl, sessionId]: { value: string }[]) => {
                if (aesUrl.value && sessionId.value) {
                    this.sessionService.update({
                        aesUrl: aesUrl.value,
                        sessionId: sessionId.value
                    });
                    return of(true);
                }

                // Sin sesión: redirigir al login
                this.navController.navigateRoot('/login');
                return of(false);
            })
        );
    }

    private async getStoredCredentials(): Promise<{ value: string }[]> {
        return Promise.all([
            Preferences.get({ key: AES_URL_STORAGE_KEY }),
            Preferences.get({ key: SESSION_STORAGE_KEY })
        ]);
    }
}
